import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Stack, Typography } from '@mui/material';

import FollowUpIcon from 'src/assets/svg/followupinspirations/FollowUpIcon.svg?react';
import { LANGUAGE_MAP } from 'src/constants/language';
import useCustomMedia from 'src/hooks/useCustomMedia';
import useSurveyLogoAndName from 'src/hooks/useSurveyLogoAndName';
import LanguageSwitcher from 'src/components/common/LanguageSwitcher/index';

import { styles } from './index.styles';

export const FI_HEADER_DESKTOP_TEST_ID = 'fi-header-desktop';
export const FI_HEADER_DESKTOP_LOGO_TEST_ID = 'fi-header-desktop-logo';

export interface FIHeaderDesktopProps {
  title: string;
}

export const FIHeaderDesktop: FC<FIHeaderDesktopProps> = ({ title }) => {
  const { t } = useTranslation('common');
  const { surveyLogo, surveyName } = useSurveyLogoAndName();
  const { headerLogoPath } = useCustomMedia();
  const languages = [
    LANGUAGE_MAP[0].abbreviation, // English (Englisch)
    LANGUAGE_MAP[1].abbreviation, // German (Deutsch)
  ];

  return (
    <Stack sx={styles.headerContainer} data-test-id={FI_HEADER_DESKTOP_TEST_ID}>
      <Grid container sx={styles.headerBanner}>
        <Grid
          item
          xs={4}
          sx={{
            justifyContent: 'flex-start',
          }}
        >
          <Box sx={styles.brandLogoWrapper}>
            {!!headerLogoPath && (
              <img
                alt={t('mainLogo')}
                src={headerLogoPath}
                height="100%"
                width="auto"
                data-test-id={FI_HEADER_DESKTOP_LOGO_TEST_ID}
              />
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={4}
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={styles.surveyLogo}>
            {surveyLogo && (
              <img alt={t('appLogo')} src={surveyLogo} height="100%" />
            )}
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              {surveyName}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={4}>
          <Stack sx={styles.followUpIconWrapper}>
            <FollowUpIcon fontSize={'2rem'} />
            <Typography variant="h4">{title}</Typography>
          </Stack>
        </Grid>
      </Grid>
      <Grid sx={styles.langSwitcher}>
        <LanguageSwitcher languageOverride={languages} color="secondary" />
      </Grid>
    </Stack>
  );
};

export default FIHeaderDesktop;
